import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getCategorias,
  createCategoria,
  updateCategoria,
} from "../../../services/usuarios_tienda/categorias";
import {
  getSubcategorias,
  createSubcategoria,
  updateSubcategoria,
} from "../../../services/usuarios_tienda/subcategorias";
import CategoryFormModal from "./CategoryFormModal";
import SubcategoryFormModal from "./SubcategoryFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./CategoriesPage.css";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const VISIBLE_PAGE_COUNT = 3;
/** Altura máxima del panel de subcategorías antes de mostrar scroll interno (~6 filas). */
const SUBCATEGORY_PANEL_MAX_HEIGHT_PX = 264;

const CATEGORY_STATUS_FILTER_OPTIONS = [
  { key: "ACTIVO", label: "Activos", tone: "activo" },
  { key: "INACTIVO", label: "Inactivos", tone: "inactivo" },
  { key: "ALL", label: "Todos", tone: "all" },
];

function matchesStatusFilter(item, filter) {
  if (filter === "ALL") return true;
  if (filter === "ACTIVO") return Number(item.estado) === 1;
  if (filter === "INACTIVO") return Number(item.estado) !== 1;
  return true;
}

function CategoriesPage() {
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVO");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [showModal, setShowModal] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subModalCategoria, setSubModalCategoria] = useState(null);
  const [selectedSubcategoria, setSelectedSubcategoria] = useState(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const canManageSubcategories =
    hasPerm("view_subcategories") || hasPerm("create_categories") || hasPerm("update_categories");

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [cats, subs] = await Promise.all([getCategorias(), getSubcategorias()]);
      setCategorias(Array.isArray(cats) ? cats : []);
      setSubcategorias(Array.isArray(subs) ? subs : []);
    } catch (error) {
      console.error(error);
      showAlert("error", error.message);
    }
  }, [showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const subsByCategoria = useMemo(() => {
    const map = new Map();
    subcategorias.forEach((sub) => {
      const key = String(sub.categoria_producto_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(sub);
    });
    map.forEach((list) => {
      list.sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
    });
    return map;
  }, [subcategorias]);

  const getVisibleSubs = useCallback(
    (categoriaId) => {
      const subs = subsByCategoria.get(String(categoriaId)) || [];
      const q = search.trim().toLowerCase();
      return subs.filter((sub) => {
        const matchesStatus = matchesStatusFilter(sub, statusFilter);
        const matchesSearch = !q || String(sub.nombre || "").toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      });
    },
    [subsByCategoria, search, statusFilter]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return categorias.filter((c) => {
      const matchesStatus = matchesStatusFilter(c, statusFilter);
      if (!matchesStatus) return false;

      const nameMatch = !q || String(c?.nombre || "").toLowerCase().includes(q);
      if (nameMatch) return true;

      const subs = subsByCategoria.get(String(c.categoria_producto_id)) || [];
      return subs.some(
        (sub) =>
          matchesStatusFilter(sub, statusFilter) &&
          String(sub.nombre || "").toLowerCase().includes(q)
      );
    });
  }, [categorias, search, statusFilter, subsByCategoria]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) return;

    const toExpand = new Set();
    categorias.forEach((c) => {
      const subs = subsByCategoria.get(String(c.categoria_producto_id)) || [];
      if (
        subs.some(
          (sub) =>
            matchesStatusFilter(sub, statusFilter) &&
            String(sub.nombre || "").toLowerCase().includes(q)
        )
      ) {
        toExpand.add(c.categoria_producto_id);
      }
    });

    if (toExpand.size > 0) {
      setExpandedIds((prev) => new Set([...prev, ...toExpand]));
    }
  }, [search, categorias, subsByCategoria, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const visiblePages = useMemo(() => {
    const total = totalPages;
    if (total <= VISIBLE_PAGE_COUNT) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, page - Math.floor(VISIBLE_PAGE_COUNT / 2));
    let endPage = startPage + VISIBLE_PAGE_COUNT - 1;
    if (endPage > total) {
      endPage = total;
      startPage = endPage - VISIBLE_PAGE_COUNT + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [page, totalPages]);

  const goToPage = (pageNumber) => {
    if (pageNumber === page || pageNumber < 1 || pageNumber > totalPages) return;
    setPage(pageNumber);
  };

  const nextPage = () => hasNext && setPage(page + 1);
  const prevPage = () => hasPrev && setPage(page - 1);

  const toggleExpanded = (categoriaId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) next.delete(categoriaId);
      else next.add(categoriaId);
      return next;
    });
  };

  const openCreateModal = () => {
    setSelectedCategoria(null);
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setSelectedCategoria(cat);
    setShowModal(true);
  };

  const openCreateSubModal = (cat) => {
    setSubModalCategoria(cat);
    setSelectedSubcategoria(null);
    setShowSubModal(true);
  };

  const openEditSubModal = (cat, sub) => {
    setSubModalCategoria(cat);
    setSelectedSubcategoria(sub);
    setShowSubModal(true);
  };

  const handleCreate = async (formData) => {
    const res = await createCategoria(formData);
    await loadData();
    setShowModal(false);
    showAlert("success", res?.message || "Categoría creada correctamente.");
  };

  const handleUpdate = async (id, formData) => {
    const res = await updateCategoria(id, formData);
    await loadData();
    setShowModal(false);
    showAlert("success", res?.message || "Categoría actualizada correctamente.");
  };

  const handleCreateSub = async (formData) => {
    const res = await createSubcategoria(formData);
    await loadData();
    setShowSubModal(false);
    setExpandedIds((prev) => new Set([...prev, formData.categoria_producto_id]));
    showAlert("success", res?.message || "Subcategoría creada correctamente.");
  };

  const handleUpdateSub = async (id, formData) => {
    const res = await updateSubcategoria(id, formData);
    await loadData();
    setShowSubModal(false);
    showAlert("success", res?.message || "Subcategoría actualizada correctamente.");
  };

  const colCount = 5;

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button
            type="button"
            className="omx-alert__x"
            onClick={() => setAlert(null)}
            aria-label="Cerrar alerta"
          >
            ×
          </button>
        </div>
      )}

      <div className="header">
        <div className="categories-header__row">
          <h2 className="title">Gestión de categorías</h2>

          {hasPerm("create_categories") ? (
            <button type="button" className="categories-btn-create" onClick={openCreateModal}>
              Crear categoría
            </button>
          ) : null}
        </div>

        <p className="page-lead">
          Administra categorías y sus subcategorías desde un solo lugar. Expande cada fila para ver
          o editar las subcategorías.
        </p>

        <CategoryStatusFilterBar value={statusFilter} onChange={setStatusFilter} />

        <div className="categories-filters">
          <div className="categories-filters__group categories-filters__group--search">
            <label className="categories-filters__label" htmlFor="categories-search">
              Buscar
            </label>
            <div className="categories-filters__search-wrap">
              <input
                id="categories-search"
                type="text"
                placeholder="Categoría o subcategoría…"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim() ? (
                <button
                  type="button"
                  className="categories-filters__clear"
                  onClick={() => setSearch("")}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
          <table className="modern-table categories-table tienda-table--cards">
            <thead>
              <tr>
                <th className="th-col-expand" aria-label="Expandir" />
                <th>Nombre</th>
                <th>Estado</th>
                <th className="categories-col-fecha">Fecha de creación</th>
                <th className="th-col-actions" style={{ textAlign: "right" }}>
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="no-data">
                    No se encontraron categorías
                  </td>
                </tr>
              ) : (
                paginated.map((c) => {
                  const categoriaId = c.categoria_producto_id;
                  const isExpanded = expandedIds.has(categoriaId);
                  const allSubs = subsByCategoria.get(String(categoriaId)) || [];
                  const visibleSubs = getVisibleSubs(categoriaId);
                  const hasSubs = allSubs.length > 0;

                  return (
                    <CategoryRows
                      key={categoriaId}
                      categoria={c}
                      colCount={colCount}
                      isExpanded={isExpanded}
                      hasSubs={hasSubs}
                      visibleSubs={visibleSubs}
                      totalSubsCount={allSubs.length}
                      canManageSubcategories={canManageSubcategories}
                      onToggle={() => toggleExpanded(categoriaId)}
                      onEditCategory={() => openEditModal(c)}
                      onCreateSub={() => openCreateSubModal(c)}
                      onEditSub={(sub) => openEditSubModal(c, sub)}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="pagination">
            <div className="pagination__center">
              <div className="pagination__nav" aria-label="Paginación de categorías">
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={prevPage}
                  disabled={!hasPrev}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className="pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`pagination__page${
                        pageNumber === page ? " is-active" : ""
                      }`}
                      onClick={() => goToPage(pageNumber)}
                      aria-label={`Ir a página ${pageNumber}`}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={nextPage}
                  disabled={!hasNext}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <div className="pagination__size">
                <span className="pagination__size-label">Mostrar</span>
                <select
                  className="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  title="Filas por página"
                  aria-label="Filas por página"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="pagination__size-label">por página</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <CategoryFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          categoriaData={selectedCategoria}
          onSubmit={async (formData) => {
            if (selectedCategoria) {
              await handleUpdate(selectedCategoria.categoria_producto_id, formData);
            } else {
              await handleCreate(formData);
            }
          }}
        />
      )}

      {showSubModal && subModalCategoria && (
        <SubcategoryFormModal
          isOpen={showSubModal}
          onClose={() => setShowSubModal(false)}
          categoria={subModalCategoria}
          subcategoriaData={selectedSubcategoria}
          onSubmit={async (formData) => {
            if (selectedSubcategoria) {
              await handleUpdateSub(selectedSubcategoria.subcategorias_id, formData);
            } else {
              await handleCreateSub(formData);
            }
          }}
        />
      )}
    </div>
  );
}

function CategoryRows({
  categoria,
  colCount,
  isExpanded,
  hasSubs,
  visibleSubs,
  totalSubsCount,
  canManageSubcategories,
  onToggle,
  onEditCategory,
  onCreateSub,
  onEditSub,
}) {
  return (
    <>
      <tr className={`categories-row categories-row--parent${isExpanded ? " is-expanded" : ""}`}>
        <td className="categories-cell-expand">
          <button
            type="button"
            className={`categories-expand${isExpanded ? " is-open" : ""}`}
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? `Ocultar subcategorías de ${categoria.nombre}`
                : `Ver subcategorías de ${categoria.nombre}`
            }
          >
            <span className="categories-expand__icon" aria-hidden>
              ›
            </span>
          </button>
        </td>
        <td data-label="Nombre">
          <div className="categories-name-cell">
            <span className="categories-name-cell__title">{categoria.nombre}</span>
            {totalSubsCount > 0 ? (
              <span className="categories-name-cell__badge">
                {totalSubsCount} subcategoría{totalSubsCount === 1 ? "" : "s"}
              </span>
            ) : (
              <span className="categories-name-cell__badge categories-name-cell__badge--empty">
                Sin subcategorías
              </span>
            )}
          </div>
        </td>
        <td data-label="Estado">
          <span
            className={Number(categoria.estado) === 1 ? "status active" : "status inactive"}
          >
            {Number(categoria.estado) === 1 ? "Activo" : "Inactivo"}
          </span>
        </td>
        <td className="categories-col-fecha" data-label="Fecha de creación">
          {categoria.fecha_creado?.slice(0, 10)}
        </td>
        <td className="categories-col-actions" data-label="Acciones" style={{ textAlign: "right" }}>
          {hasPerm("update_categories") && (
            <span
              className="action-icon edit"
              onClick={onEditCategory}
              title="Editar categoría"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onEditCategory()}
            >
              {IconEdit}
            </span>
          )}
        </td>
      </tr>

      {isExpanded && (
        <>
          {visibleSubs.length === 0 ? (
            <tr className="categories-row categories-row--sub-empty">
              <td colSpan={colCount}>
                <div className="categories-sub-panel">
                  <p className="categories-sub-panel__empty">
                    {hasSubs
                      ? "No hay subcategorías que coincidan con el filtro actual."
                      : "Esta categoría aún no tiene subcategorías."}
                  </p>
                  {canManageSubcategories ? (
                    <button
                      type="button"
                      className="categories-sub-add"
                      onClick={onCreateSub}
                    >
                      + Agregar subcategoría
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          ) : (
            <tr className="categories-row categories-row--sub-wrap">
              <td colSpan={colCount} className="categories-sub-wrap-cell">
                <div
                  className={`categories-sub-scroll${
                    visibleSubs.length > 6 ? " categories-sub-scroll--tall" : ""
                  }`}
                  style={{ maxHeight: SUBCATEGORY_PANEL_MAX_HEIGHT_PX }}
                >
                  <table className="categories-sub-inner-table">
                    <tbody>
                      {visibleSubs.map((sub) => (
                        <tr
                          key={sub.subcategorias_id}
                          className="categories-row categories-row--sub"
                        >
                          <td className="categories-cell-expand" />
                          <td data-label="Nombre">
                            <span className="categories-sub-name">{sub.nombre}</span>
                          </td>
                          <td data-label="Estado">
                            <span
                              className={
                                Number(sub.estado) === 1 ? "status active" : "status inactive"
                              }
                            >
                              {Number(sub.estado) === 1 ? "Activo" : "Inactivo"}
                            </span>
                          </td>
                          <td className="categories-col-fecha" data-label="Fecha de creación">
                            {sub.fecha_creado?.slice(0, 10)}
                          </td>
                          <td className="categories-col-actions" data-label="Acciones" style={{ textAlign: "right" }}>
                            {canManageSubcategories ? (
                              <span
                                className="action-icon edit"
                                onClick={() => onEditSub(sub)}
                                title="Editar subcategoría"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && onEditSub(sub)}
                              >
                                {IconEdit}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="categories-sub-footer">
                  {visibleSubs.length > 6 ? (
                    <span className="categories-sub-footer__hint">
                      {visibleSubs.length} subcategorías — desplázate dentro del panel
                    </span>
                  ) : (
                    <span className="categories-sub-footer__hint" aria-hidden />
                  )}
                  {canManageSubcategories ? (
                    <button
                      type="button"
                      className="categories-sub-add"
                      onClick={onCreateSub}
                    >
                      + Agregar subcategoría
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
}

function CategoryStatusFilterBar({ value, onChange }) {
  return (
    <div className="categories-status-filter" role="group" aria-label="Filtrar por estado">
      <span className="categories-status-filter__label">Ver</span>
      <div className="categories-status-filter__options">
        {CATEGORY_STATUS_FILTER_OPTIONS.map((opt) => {
          const toneClass = opt.tone && opt.tone !== "all" ? ` is-${opt.tone}` : "";
          return (
            <button
              key={opt.key}
              type="button"
              className={`categories-status-filter__btn${
                value === opt.key ? " is-active" : ""
              }${toneClass}`}
              aria-pressed={value === opt.key}
              onClick={() => onChange(opt.key)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default CategoriesPage;
